import { isMacOs } from "./isMacOs";

export type Platform = "mac" | "windows" | "linux" | "other";

export function getPlatform(): Platform {
  try {
    if (isMacOs()) return "mac";
    const platform = navigator.platform?.toLowerCase?.() ?? "";
    if (platform.includes("win")) return "windows";
    if (platform.includes("linux")) return "linux";
  } catch {}
  return "other";
}

export type KeyChord = {
  key: string;
  meta?: boolean;
  ctrl?: boolean;
  alt?: boolean;
  shift?: boolean;
};

export type PlatformKeyChord = {
  mac: KeyChord;
  windows: KeyChord;
  linux?: KeyChord;
  other?: KeyChord;
};

type KeyEventLike = Pick<KeyboardEvent, "key" | "metaKey" | "ctrlKey" | "altKey" | "shiftKey">;

export function getPlatformChord(chords: PlatformKeyChord, platform: Platform = getPlatform()): KeyChord {
  if (platform === "mac") return chords.mac;
  if (platform === "windows") return chords.windows;
  if (platform === "linux") return chords.linux ?? chords.windows;
  return chords.other ?? chords.windows;
}

export function eventMatchesChord(e: KeyEventLike, chord: KeyChord): boolean {
  const keyMatches = e.key === chord.key;
  if (!keyMatches) return false;

  const wantMeta = Boolean(chord.meta);
  const wantCtrl = Boolean(chord.ctrl);
  const wantAlt = Boolean(chord.alt);
  const wantShift = Boolean(chord.shift);

  return (
    e.metaKey === wantMeta &&
    e.ctrlKey === wantCtrl &&
    e.altKey === wantAlt &&
    e.shiftKey === wantShift
  );
}

export function eventMatchesPlatformChord(
  e: KeyEventLike,
  chords: PlatformKeyChord,
  platform: Platform = getPlatform(),
): boolean {
  return eventMatchesChord(e, getPlatformChord(chords, platform));
}

function formatKeyNameForPlatform(key: string, platform: Platform): string {
  if (key === "Backspace") return platform === "mac" ? "⌫" : "Backspace";
  if (key === "Delete") return platform === "mac" ? "⌦" : "Delete";
  if (key === "Enter") return platform === "mac" ? "↩" : "Enter";
  if (key === " ") return platform === "mac" ? "Space" : "Space";
  return key.length === 1 ? key.toUpperCase() : key;
}

export function formatChord(chord: KeyChord, platform: Platform = getPlatform()): string {
  const parts: string[] = [];
  if (platform === "mac") {
    if (chord.ctrl) parts.push("⌃");
    if (chord.alt) parts.push("⌥");
    if (chord.shift) parts.push("⇧");
    if (chord.meta) parts.push("⌘");
    parts.push(formatKeyNameForPlatform(chord.key, platform));
    return parts.join("");
  }

  if (chord.ctrl) parts.push("Ctrl");
  if (chord.alt) parts.push("Alt");
  if (chord.shift) parts.push("Shift");
  if (chord.meta) parts.push("Meta");
  parts.push(formatKeyNameForPlatform(chord.key, platform));
  return parts.join("+");
}

export function formatPlatformChord(chords: PlatformKeyChord, platform: Platform = getPlatform()): string {
  return formatChord(getPlatformChord(chords, platform), platform);
}

